import { Label } from '@radix-ui/react-label';
import {
  cloneElement,
  isValidElement,
  useId,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../cn';

// Brief §6: labels in `label` typography (12px, weight 500, uppercase, tracked 0.05em).
// Inline error chip below the field uses ErrorChip; no separate `aria-describedby` required
// because errors get role="alert" and live-region semantics from ErrorChip.
//
// This is a thin association helper: it generates a stable id, threads it into the
// child input, and wires the Radix Label htmlFor.

export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode;
  /** Help text rendered between label and input (e.g., password policy hint). */
  hint?: ReactNode;
  /** Inline error rendered below the input. Pass an ErrorChip or a string. */
  error?: ReactNode;
  /** The input element (Input, PasswordInput, etc.). */
  children: ReactElement<{ id?: string; 'aria-invalid'?: boolean }>;
  /** Optional right-aligned slot inline with the label (e.g., "Forgot password?" link). */
  trailing?: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  children,
  trailing,
  className,
  ...rest
}: FieldProps) {
  const reactId = useId();
  const id = children.props.id ?? reactId;
  const childPatch: { id: string; 'aria-invalid'?: boolean } = { id };
  const ariaInvalid = error ? true : children.props['aria-invalid'];
  if (ariaInvalid !== undefined) childPatch['aria-invalid'] = ariaInvalid;
  const child = isValidElement(children) ? cloneElement(children, childPatch) : children;
  return (
    <div className={cn('flex flex-col gap-2', className)} {...rest}>
      <div className="flex items-baseline justify-between gap-3">
        <Label
          htmlFor={id}
          className="text-[12px] font-medium uppercase tracking-[0.05em] text-ink-mid"
        >
          {label}
        </Label>
        {trailing && (
          <div className="text-[12px] uppercase tracking-[0.05em]">{trailing}</div>
        )}
      </div>
      {hint && <p className="text-[12px] leading-[1.4] text-ink-low">{hint}</p>}
      {child}
      {error && <div>{error}</div>}
    </div>
  );
}
